<template>
  <div
    class="px-8 pt-3 md:px-24"
  >
    <settings-header
      active-tab="columns"
      :entity-id="entity.id"
    />
    <div class="flex justify-between mt-10">
      <h1
        class="text-xl text-left text-sc-gray-dark"
      >
        {{ $t('message.settings.columnsTab.customColumns') }}
      </h1>
      <button
        ref="add-column-button"
        class="inline-block px-3 py-1 text-sm leading-5 text-center text-white rounded bg-sc-blue hover:text-white"
        @click="showAddExtraColumnModal()"
      >
        {{ $t('message.settings.columnsTab.addColumn') }}
      </button>
    </div>
    <div class="mt-5">
      <b-table
        :data="extraColumns"
        class="border-t border-b border-sc-gray-light"
        :row-class="() => 'group'"
        hoverable
      >
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="name"
          :label="columnFormatter($t('message.settings.columnsTab.table.header.name'))"
          v-slot="props"
          cell-class="border-0"
        >
          <p
            class="text-sm text-primary"
          >
            {{ props.row.name }}
          </p>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="type"
          :label="columnFormatter($t('message.settings.columnsTab.table.header.type'))"
          v-slot="props"
          cell-class="border-0"
        >
          <p
            class="text-sm text-primary"
          >
            {{ newExtraColumnTypeMap[props.row.type].name }}
          </p>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="options"
          :label="columnFormatter($t('message.settings.columnsTab.table.header.options'))"
          v-slot="props"
          cell-class="border-0"
        >
          <b-taglist
            v-if="props.row.type === 'Enum'"
          >
            <b-tag
              v-for="(tag, key) in props.row.options.tags"
              :key="key"
              type="is-primary"
              rounded
            >
              {{ tag }}
            </b-tag>
          </b-taglist>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="default"
          :label="columnFormatter($t('message.settings.columnsTab.table.header.defaultValue'))"
          v-slot="props"
          cell-class="border-0"
        >
          <component
            :is="newExtraColumnTypeMap[props.row.type].show.component"
            v-bind="newExtraColumnTypeMap[props.row.type].show.props(props.row)"
          >
            <p
              :class="{'text-sm text-primary': props.row.type !== 'Enum' }"
            >
              {{ props.row.default }}
            </p>
          </component>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="name"
          :label="columnFormatter($t('message.settings.columnsTab.table.header.activateFilters'))"
          v-slot="props"
          cell-class="border-0"
        >
          <b-switch
            class="text-sm text-primary"
            :value="props.row.enableFilter"
            @input="(value) => activateExtraColumnFilter(props.row, value)"
            v-if="props.row.type !== 'String'"
          >
            {{ props.row.enableFilter ? $t('message.settings.columnsTab.table.enabledFilter') :
              $t('message.settings.columnsTab.table.disabledFilter') }}
          </b-switch>
        </b-table-column>
        <b-table-column
          field="remove"
          v-slot="props"
        >
          <button
            ref="edit-column-button"
            class="visible px-5 py-1 text-xs text-white rounded md:invisible bg-sc-red group-hover:visible shadow-sc-button"
            @click="showEditExtraColumnModal(props.row.name)"
          >
            {{ $t('message.settings.columnsTab.table.editButton') }}
          </button>
          <button
            class="visible px-5 py-1 text-xs text-white rounded md:invisible bg-sc-red group-hover:visible shadow-sc-button"
            @click="showDeleteExtraColumnModal(props.row.name)"
          >
            {{ $t('message.settings.columnsTab.table.removeButton') }}
          </button>
        </b-table-column>
      </b-table>
    </div>
    <div class="mt-10 mb-20">
      <h1
        class="text-xl text-left text-sc-gray-dark"
      >
        {{ $t('message.settings.columnsTab.hiddenColumns') }}
      </h1>
      <div class="flex items-center mt-5">
        <multiselect
          v-model="entityConfiguration.hiddenColumns"
          :placeholder="$t('message.settings.columnsTab.hiddenColumnsPlaceholder')"
          :options="Object.keys(this.columns)"
          :custom-label="key => this.columns[key]"
          :multiple="true"
          :show-labels="false"
          :searchable="false"
          :hide-selected="true"
        >
          <span
            slot="caret"
            class="absolute right-0 mt-3 mr-4"
          >
            <img
              class="transform chevron"
              :src="require('@assets/images/chevron-down-outline.svg')"
            >
          </span>
        </multiselect>
        <button
          ref="hide-columns-button"
          class="inline-block px-3 py-2 ml-3 text-sm leading-5 text-center text-white rounded bg-sc-blue hover:text-white"
          @click="updateHiddenColumns()"
        >
          {{ $t('message.settings.columnsTab.updateHiddenColumns') }}
        </button>
      </div>
    </div>
    <modal
      ref="extra-column-modal"
      name="extra-column-modal"
      height="auto"
      :focus-trap="false"
      classes="overflow-visible"
      adaptive
    >
      <extra-columns-modal
        @close="closeExtraColumnModal"
        :entity-configuration="entityConfiguration"
        :selected-extra-column="selectedExtraColumn"
      />
    </modal>
    <confirmation-modal
      ref="deleteModal"
      :content="`¿Estás seguro que deseas eliminar la columna ${columnToDelete}?`"
      flip-buttons-style
      :is-approval-modal="false"
      @confirmation="deleteExtraColumn(columnToDelete)"
    />
  </div>
</template>
<script>
import { decamelize } from 'humps';
import columnFormatter from '../../helpers/columnFormatter';
import EntitiesApi from '../api/entities';
import EntityConfigurationsApi from '../api/entity_configurations';
import SettingsHeader from './settings-header.vue';
import ConfirmationModal from './confirmation-modal.vue';
import ExtraColumnsModal from './extra-columns-modal.vue';

export default {
  components: {
    SettingsHeader,
    ConfirmationModal,
    ExtraColumnsModal,
  },
  props: {
    entity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      entityConfiguration: {
        hiddenColumns: [],
        extraColumnsSchemaArray: [],
      },
      columns: {
        postDate: 'Fecha',
        account: 'Cuenta',
        rut: 'RUT emisor',
        senderName: 'Nombre Emisor',
        description: 'Descripción',
        amount: 'Monto',
        status: 'Estado',
        detail: 'Detalle',
      },
      selectedExtraColumn: null,
      newExtraColumnTypeMap: {
        'Boolean': {
          name: this.$t('message.settings.columnsTab.types.toggle'), default: false,
          create: { component: 'b-checkbox', props: () => null },
          show: { component: 'b-icon',
            props: (row) => ({ type: 'is-primary', icon: row.default ? 'checkbox-marked' : 'checkbox-blank' }),
          },
        },
        'Date': {
          name: this.$t('message.settings.columnsTab.types.date'), default: null,
          create: { component: 'b-datepicker', props: () => ({
            placeholder: this.$t('message.settings.columnsTab.modal.optionalDefaultValuePlaceholder'),
            size: 'is-small',
            firstDayOfWeek: 1,
            editable: true,
          }) },
          show: { component: 'span', props: () => null },
        },
        'Enum': {
          name: this.$t('message.settings.columnsTab.types.tags'), default: [],
          create: { component: 'multiselect', props: () => ({
            placeholder: this.$t('message.settings.columnsTab.modal.defaultValuePlaceholder'),
            options: Object.values(this.extraColumnOptions),
            multiple: false,
            searchable: false,
            showLabels: false,
            hideSelected: true,
          }) },
          show: { component: 'b-tag', props: () => ({ type: 'is-primary', rounded: true }) },
        },
        'String': {
          name: this.$t('message.settings.columnsTab.types.text'), default: '',
          create: { component: 'b-input', props: () => ({
            placeholder: this.$t('message.settings.columnsTab.modal.defaultValuePlaceholder'),
            size: 'is-small',
          }) },
          show: { component: 'span', props: () => null },
        },
      },
      columnToDelete: '',
    };
  },
  computed: {
    extraColumns() {
      return this.entityConfiguration.extraColumnsSchemaArray
        .map(description =>
          ({ name: description.name,
            type: description.type,
            default: description.default,
            options: description.options,
            enableFilter: description.enableFilter || false,
          }),
        );
    },
  },
  beforeMount() {
    this.$store.dispatch('setEntityId', this.entity.id);
    this.reloadConfiguration();
  },
  methods: {
    async save() {
      this.loading = true;
      await EntitiesApi.updateEntityConfiguration(this.entity.id, this.entityConfiguration);
      this.loading = false;
    },
    reloadConfiguration() {
      this.$store.dispatch('getEntityConfiguration').then(() => {
        this.entityConfiguration = this.$store.state.entities.entityConfiguration;
      });
    },
    async activateExtraColumnFilter(extraColumnSchema, value) {
      this.loading = true;
      try {
        await EntityConfigurationsApi.enableFilterExtraColumn(this.entityConfiguration.id,
          extraColumnSchema.name,
          value);
        this.reloadConfiguration();
      } catch (error) {
        this.loading = false;
        console.log(error.message);
      }
    },
    async deleteExtraColumn(extraColumnName) {
      this.loading = true;
      await EntityConfigurationsApi.deleteExtraColumn(this.entityConfiguration.id, { extraColumnName });
      this.columnToDelete = '';
      this.reloadConfiguration();
      this.loading = false;
    },
    updateHiddenColumns() {
      const hiddenColumns = this.entityConfiguration.hiddenColumns.map(col => decamelize(col));
      EntityConfigurationsApi.updateHiddenColumns(this.entityConfiguration.id, hiddenColumns);
    },
    async showEditExtraColumnModal(extraColumnName) {
      this.selectedExtraColumn = this.extraColumns.find((column) => column.name === extraColumnName);
      this.$modal.show('extra-column-modal');
    },
    async showAddExtraColumnModal() {
      this.selectedExtraColumn = null;
      this.$modal.show('extra-column-modal');
    },
    closeExtraColumnModal() {
      this.reloadConfiguration();
      this.loading = false;
      this.$modal.hide('extra-column-modal');
    },
    showDeleteExtraColumnModal(columnName) {
      this.columnToDelete = columnName;
      this.$refs.deleteModal.show();
    },
    columnFormatter,
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>

.multiselect {
  @apply text-sm;
}

.multiselect__tags {
  min-height: 31px;
  @apply border-0;
  @apply pt-1;
  @apply pb-1;
}

.multiselect__tags,
.multiselect__content-wrapper {
  @apply shadow-sc-button;
}

.multiselect__content-wrapper {
  @apply border-0;
}

.multiselect__tag,
.multiselect__option--highlight {
  @apply bg-sc-blue;
}

.multiselect__tag {
  @apply rounded-full;
}

.multiselect__tag-icon:hover,
.multiselect__option::after {
  @apply bg-sc-blue;
}

.multiselect__tag-icon::after {
  @apply text-white;
}

.multiselect__input {
  @apply text-sm;
}

.multiselect--active span .chevron {
  @apply rotate-180;
}

.is-small.input {
  @apply text-sm;
  @apply rounded;
}

.datepicker-footer {
  @apply hidden;
}
</style>
